import { toast } from 'sonner';
import { useSelector } from 'react-redux';
import { COLLECTION_KINDS } from '../../../../../../constants/collections-v2/collectionKinds';
import {
  connectionMeetingWindowsProblem,
  normaliseConnectionMeetings,
} from '../../../../../../constants/agents/connectionAgent';
import { PROGRAMME_FORMATS, useEventCollections } from './useConnectionAgentDefineHandler';

const useConnectionAgentValidator = () => {
  const { title, formats, sessionsCollectionId, attendeeCollectionId, rules, meetings, existing } =
    useSelector(state => state.agent.connectionAgent);

  // Only ready rows are matched: rows missing a required field are left out until they're complete.
  const { collections: attendeeCollections } = useEventCollections(COLLECTION_KINDS.attendees);
  const counts =
    attendeeCollections.find(collection => collection._id === attendeeCollectionId)?.collectionData
      ?.recordCounts || {};
  const delegateCount = attendeeCollectionId ? (counts.readyDelegates ?? 0) : 0;
  const sponsorCount = attendeeCollectionId ? (counts.readySponsors ?? 0) : 0;
  const flaggedCount = attendeeCollectionId ? (counts.missing ?? 0) : 0;
  // Editing an agent after it's made: the people already in it count too (nobody is removed, D4).
  const delegatesToMatch = Math.max(delegateCount, existing?.delegates || 0);
  const sponsorsToMatch = Math.max(sponsorCount, existing?.sponsors || 0);

  const isConnectionDefineNextButtonDisabled = () => {
    if (!title?.trim()) {
      toast.warning('Enter a name for this agent');
      return true;
    }
    if (!formats.length) {
      toast.warning('Turn on at least one format');
      return true;
    }
    if (formats.some(slot => !slot.engineId)) {
      toast.warning('Pick a matching engine for every format');
      return true;
    }
    if (formats.some(slot => PROGRAMME_FORMATS.includes(slot.format)) && !sessionsCollectionId) {
      toast.warning('Pick the event collection with the programme');
      return true;
    }
    if (!attendeeCollectionId) {
      toast.warning('Pick the attendee collection');
      return true;
    }
    if (!delegatesToMatch) {
      toast.warning('No delegates ready to match', {
        description:
          'At least one delegate with every required field is needed. Complete them in the Event Database.',
      });
      return true;
    }
    const meetsSponsors = formats.some(
      slot => slot.format === 'one_to_one' && slot.pairing === 'delegate_sponsor'
    );
    if (meetsSponsors && !sponsorsToMatch) {
      toast.warning('No sponsors in the list', {
        description:
          'Delegate and sponsor meetings need sponsors. Add them to the attendee collection, or pair delegates with each other.',
      });
      return true;
    }
    return false;
  };

  const isConnectionRulesNextButtonDisabled = () => {
    if (!rules?.pickCap || rules.pickCap < 1) {
      toast.warning('Set how many people each person can pick');
      return true;
    }
    if (rules.search && rules.searchCap != null && rules.searchCap < 1) {
      toast.warning('Requests from search must be at least 1', {
        description: 'Leave it empty for no limit.',
      });
      return true;
    }
    if (rules.confirmCap != null && rules.confirmCap < 1) {
      toast.warning('Accepts per responder must be at least 1', {
        description: 'Leave it empty for no limit.',
      });
      return true;
    }
    const layout = normaliseConnectionMeetings(meetings);
    if (!layout.durationOptions.length) {
      toast.warning('Offer at least one meeting length');
      return true;
    }
    if (!layout.durationOptions.includes(layout.defaultDuration)) {
      toast.warning('Pick a default length from the lengths offered');
      return true;
    }
    if (!layout.bufferOptions.includes(layout.defaultBuffer)) {
      toast.warning('Pick a default gap from the gaps offered');
      return true;
    }
    if (!meetings?.dayStart || !meetings?.dayEnd || meetings.dayStart >= meetings.dayEnd) {
      toast.warning('Meeting hours must end after they start');
      return true;
    }
    const windowsProblem = connectionMeetingWindowsProblem(meetings?.windows);
    if (windowsProblem) {
      toast.warning(windowsProblem);
      return true;
    }
    return false;
  };

  return {
    delegateCount,
    sponsorCount,
    flaggedCount,
    delegatesToMatch,
    sponsorsToMatch,
    isConnectionDefineNextButtonDisabled,
    isConnectionRulesNextButtonDisabled,
  };
};

export default useConnectionAgentValidator;
